import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import { 
  FileText, 
  Plus, 
  Trash2, 
  Save,
  Calculator,
  User,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { clientsAPI, invoicesAPI, profileAPI, servicesAPI } from '../services/api';

interface InvoiceItem {
  serialNumber: number;
  description: string;
  sac: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface Client {
  _id: string;
  clientName: string;
  email: string;
  contactNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
  taxType?: 'IGST' | 'SGST_CGST';
  placeOfSupply: string;
}

interface Service {
  _id: string;
  description: string;
  sac: string;
  price: number;
}

const CreateInvoice: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [gstRate, setGstRate] = useState<number>(18);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [additionalPaymentTerms, setAdditionalPaymentTerms] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<{ [key: number]: boolean }>({});
  const [filteredSuggestions, setFilteredSuggestions] = useState<{ [key: number]: Service[] }>({});
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState<string>('');
  const [useCustomInvoiceNumber, setUseCustomInvoiceNumber] = useState<boolean>(false);
  const [customInvoiceNumber, setCustomInvoiceNumber] = useState<string>('');

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      items: [{
        description: '',
        sac: '',
        quantity: 1,
        rate: NaN
      }],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      paymentTerms: 'Payment due within 30 days'
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');
  const watchedDueDate = watch('dueDate');

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.autocomplete-wrapper')) {
        setShowSuggestions({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-update payment terms based on due date
  useEffect(() => {
    if (watchedDueDate) {
      const dueDateObj = new Date(watchedDueDate);
      const today = new Date();
      const diffTime = dueDateObj.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      const autoTerm = `Payment due within ${diffDays} days`;
      setValue('paymentTerms', autoTerm);
    }
  }, [watchedDueDate, setValue]);

  const addPaymentTerm = () => {
    setAdditionalPaymentTerms([...additionalPaymentTerms, '']);
  };

  const removePaymentTerm = (index: number) => {
    setAdditionalPaymentTerms(additionalPaymentTerms.filter((_, i) => i !== index));
  };

  const updateAdditionalPaymentTerm = (index: number, value: string) => {
    const updated = [...additionalPaymentTerms];
    updated[index] = value;
    setAdditionalPaymentTerms(updated);
  };

  const fetchInitialData = async () => {
    try {
      const [clientsRes, servicesRes, profileRes] = await Promise.all([
        clientsAPI.getAll({ limit: 100 }),
        servicesAPI.getAll({ isActive: 'true' }),
        profileAPI.get().catch(() => null)
      ]);
      
      setClients(clientsRes.data.clients);
      setServices(servicesRes.data);
      
      if (profileRes?.data) {
        if (profileRes.data.defaultGstRate) {
          setGstRate(profileRes.data.defaultGstRate);
        }
        // Calculate next invoice number
        if (profileRes.data.invoiceNumberPrefix && profileRes.data.lastInvoiceNumber !== undefined) {
          const nextNumber = profileRes.data.lastInvoiceNumber + 1;
          const invoiceNumber = `${profileRes.data.invoiceNumberPrefix}${nextNumber.toString().padStart(6, '0')}`;
          setNextInvoiceNumber(invoiceNumber);
        }
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (clientId: string) => {
    const client = clients.find(c => c._id === clientId);
    setSelectedClient(client || null);
      setValue('client' as any, clientId);
  };

  const addItem = () => {
    const newItem = {
      description: '',
      sac: '',
      quantity: 1,
      rate: NaN
    };
    append(newItem);
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const calculateAmount = (quantity: number, rate: number) => {
    return quantity * rate;
  };

  const calculateTotals = () => {
    const items = watchedItems || [];
    const subtotal = items.reduce((sum, item) => sum + calculateAmount(item.quantity || 0, item.rate || 0), 0);
    const taxableAmount = subtotal;
    
    // Determine tax type from selected client
    const taxType = selectedClient?.taxType || 'SGST_CGST';
    
    // Calculate tax rates and amounts based on tax type
    let cgstRate = 0;
    let sgstRate = 0;
    let igstRate = 0;
    
    if (taxType === 'IGST') {
      // For IGST: full GST rate as IGST
      igstRate = gstRate;
      cgstRate = 0;
      sgstRate = 0;
    } else {
      // For SGST+CGST: split GST rate equally
      cgstRate = gstRate / 2;
      sgstRate = gstRate / 2;
      igstRate = 0;
    }
    
    const cgstAmount = (taxableAmount * cgstRate) / 100;
    const sgstAmount = (taxableAmount * sgstRate) / 100;
    const igstAmount = (taxableAmount * igstRate) / 100;
    
    // Round off final total amount
    const totalAmount = Math.round(taxableAmount + cgstAmount + sgstAmount + igstAmount);
    
    return {
      subtotal,
      taxableAmount,
      cgstRate,
      sgstRate,
      igstRate,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalAmount,
      taxType
    };
  };

  const totals = calculateTotals();

  // Handle description input change for autocomplete
  const handleDescriptionChange = (index: number, value: string) => {
    setValue(`items.${index}.description`, value as any);
    
    if (value.trim() === '') {
      setShowSuggestions({ ...showSuggestions, [index]: false });
      return;
    }

    // Filter services based on description
    const filtered = services.filter(service =>
      service.description.toLowerCase().includes(value.toLowerCase())
    );
    
    setFilteredSuggestions({ ...filteredSuggestions, [index]: filtered });
    setShowSuggestions({ ...showSuggestions, [index]: filtered.length > 0 });
  };

  // Handle service selection
  const handleServiceSelect = (index: number, service: Service) => {
    setValue(`items.${index}.description`, service.description);
    setValue(`items.${index}.sac`, service.sac);
    setValue(`items.${index}.rate`, service.price);
    setShowSuggestions({ ...showSuggestions, [index]: false });
  };


  const onSubmit = async (data: any) => {
    setSaving(true);
    setMessage(null);

    try {
      // Calculate amounts for each item
      const itemsWithAmounts = data.items.map((item: any, index: any) => ({
        ...item,
        serialNumber: index + 1,
        amount: calculateAmount(item.quantity || 0, item.rate || 0)
      }));

      // Combine auto payment term with additional terms
      const allPaymentTerms = additionalPaymentTerms.filter(term => term.trim() !== '');
      const paymentTermsText = data.paymentTerms ? [data.paymentTerms, ...allPaymentTerms].join('. ') : allPaymentTerms.join('. ');

      const invoiceData = {
        ...data,
        items: itemsWithAmounts,
        paymentTerms: paymentTermsText || data.paymentTerms,
        ...totals,
        customInvoiceNumber: useCustomInvoiceNumber && customInvoiceNumber.trim() ? customInvoiceNumber.trim() : undefined
      };

      await invoicesAPI.create(invoiceData);
      setMessage({ type: 'success', text: 'Invoice created successfully!' });
      
      // Reset form
      setValue('client' as any, '');
      setValue('items', [{
        description: '',
        sac: '',
        quantity: 1,
        rate: NaN
      }]);
      setAdditionalPaymentTerms([]);
      setSelectedClient(null);
      setUseCustomInvoiceNumber(false);
      setCustomInvoiceNumber('');
      // Refresh invoice number for next invoice
      fetchInitialData();
    } catch (error) {
      console.error('Error creating invoice:', error);
      setMessage({ type: 'error', text: 'Failed to create invoice' });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <FileText className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Invoice</h1>
          <p className="text-gray-600">Generate a new invoice for your client</p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      {/* Invoice Number Display */}
      {nextInvoiceNumber && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card bg-blue-50 border-blue-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Next Invoice Number</p>
              <p className="text-2xl font-bold text-primary-600">{nextInvoiceNumber}</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomInvoiceNumber}
                  onChange={(e) => {
                    setUseCustomInvoiceNumber(e.target.checked);
                    if (!e.target.checked) {
                      setCustomInvoiceNumber('');
                    }
                  }}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Do you need to pass custom invoice number?</span>
              </label>
            </div>
          </div>
          {useCustomInvoiceNumber && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-blue-200"
            >
              <label className="form-label">Custom Invoice Number *</label>
              <input
                type="text"
                value={customInvoiceNumber}
                onChange={(e) => setCustomInvoiceNumber(e.target.value)}
                className="form-input"
                placeholder="Enter custom invoice number"
                required={useCustomInvoiceNumber}
              />
            </motion.div>
          )}
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Invoice Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Selection */}
            <div>
              <label className="form-label">Select Client *</label>
              <select
                {...register('client' as any)}
                onChange={(e) => handleClientSelect(e.target.value)}
                className="form-input"
              >
                <option value="">Choose a client...</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.clientName} - {client.email}
                  </option>
                ))}
              </select>
              {(errors as any).client && (
                <p className="mt-1 text-sm text-red-600">{(errors as any).client.message}</p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="form-label">Due Date *</label>
              <input
                type="date"
                {...register('dueDate')}
                className="form-input"
              />
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-600">{errors.dueDate.message}</p>
              )}
            </div>
          </div>

          {/* Selected Client Info */}
          {selectedClient && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-2 mb-2">
                <User className="h-5 w-5 text-gray-600" />
                <h3 className="font-medium text-gray-900">{selectedClient.clientName}</h3>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p>{selectedClient.email} • {selectedClient.contactNumber}</p>
                <p>{selectedClient.address.street}, {selectedClient.address.city}, {selectedClient.address.state} - {selectedClient.address.pincode}</p>
                {selectedClient.gstin && <p>GSTIN: {selectedClient.gstin}</p>}
                {selectedClient.taxType && (
                  <p className="mt-2 pt-2 border-t border-gray-200">
                    <span className="font-medium text-gray-900">Tax Type: </span>
                    <span className={`font-semibold ${selectedClient.taxType === 'IGST' ? 'text-blue-600' : 'text-green-600'}`}>
                      {selectedClient.taxType === 'IGST' ? 'IGST' : 'SGST + CGST'}
                    </span>
                    {selectedClient.gstin && (
                      <span className="text-gray-500 ml-2">
                        ({selectedClient.taxType === 'IGST' ? 'Different State' : 'Same State'})
                      </span>
                    )}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Invoice Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
          style={{ overflow: 'visible' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Invoice Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Item</span>
            </button>
          </div>

          <div className="overflow-x-auto" style={{ position: 'relative' }}>
            <table className="min-w-full divide-y divide-gray-200" style={{ position: 'relative' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header w-16">S.No.</th>
                  <th className="table-header">Description</th>
                  <th className="table-header w-40">SAC</th>
                  <th className="table-header w-28">Quantity</th>
                  <th className="table-header w-36">Rate</th>
                  <th className="table-header w-36">Amount</th>
                  <th className="table-header w-16">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map((field, index) => (
                  <tr key={field.id}>
                    <td className="table-cell text-center">
                      {index + 1}
                    </td>
                    <td className="table-cell" style={{ position: 'relative' }}>
                      <div className="relative autocomplete-wrapper">
                        <input
                          type="text"
                          {...register(`items.${index}.description`)}
                          onChange={(e) => handleDescriptionChange(index, e.target.value)}
                          onFocus={() => {
                            const value = watchedItems?.[index]?.description || '';
                            if (value) {
                              handleDescriptionChange(index, value);
                            }
                          }}
                          className="form-input"
                          placeholder="Enter or select service"
                          autoComplete="off"
                        />
                        {showSuggestions[index] && filteredSuggestions[index] && filteredSuggestions[index].length > 0 && (
                          <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-2xl max-h-48 overflow-auto" style={{ 
                            position: 'absolute', 
                            left: 0, 
                            bottom: '100%',
                            marginBottom: '4px'
                          }}>
                            {filteredSuggestions[index].map((service) => (
                              <div
                                key={service._id}
                                onClick={() => handleServiceSelect(index, service)}
                                className="px-4 py-2 hover:bg-primary-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors active:bg-primary-100"
                              >
                                <div className="font-medium text-gray-900">{service.description}</div>
                                <div className="text-sm text-gray-500">
                                  SAC: {service.sac} | Price: ₹{service.price.toFixed(2)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {errors.items?.[index]?.description && (
                        <p className="mt-1 text-xs text-red-600">{errors.items[index]?.description?.message}</p>
                      )}
                    </td>
                    <td className="table-cell">
                      <input
                        type="text"
                        {...register(`items.${index}.sac`)}
                        className="form-input w-full"
                        placeholder="SAC"
                      />
                    </td>
                    <td className="table-cell">
                      <input
                        type="number"
                        {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                        className="form-input w-full"
                        placeholder="Qty"
                        min="1"
                        step="1"
                      />
                      {errors.items?.[index]?.quantity && (
                        <p className="mt-1 text-xs text-red-600">{errors.items[index]?.quantity?.message}</p>
                      )}
                    </td>
                    <td className="table-cell">
                      <input
                        type="number"
                        {...register(`items.${index}.rate`, { valueAsNumber: true })}
                        className="form-input w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="Rate"
                        min="0"
                        step="0.01"
                        defaultValue=""
                      />
                      {errors.items?.[index]?.rate && (
                        <p className="mt-1 text-xs text-red-600">{errors.items[index]?.rate?.message}</p>
                      )}
                    </td>
                    <td className="table-cell font-medium">
                      {formatCurrency(calculateAmount(watchedItems?.[index]?.quantity || 0, watchedItems?.[index]?.rate || 0))}
                    </td>
                    <td className="table-cell">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        disabled={fields.length === 1}
                        className="text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Invoice Totals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-2 mb-4">
            <Calculator className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Invoice Totals</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Taxable Amount:</span>
                <span className="font-medium">{formatCurrency(totals.taxableAmount)}</span>
              </div>
              {totals.taxType === 'IGST' ? (
                <div className="flex justify-between">
                  <span className="text-gray-600">IGST ({totals.igstRate}%):</span>
                  <span className="font-medium text-blue-600">{formatCurrency(totals.igstAmount)}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">CGST ({totals.cgstRate}%):</span>
                    <span className="font-medium text-green-600">{formatCurrency(totals.cgstAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SGST ({totals.sgstRate}%):</span>
                    <span className="font-medium text-green-600">{formatCurrency(totals.sgstAmount)}</span>
                  </div>
                </>
              )}
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-lg font-bold text-primary-600">{formatCurrency(totals.totalAmount)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Notes</label>
                <textarea
                  {...register('notes' as any)}
                  rows={3}
                  className="form-input"
                  placeholder="Additional notes or terms..."
                />
              </div>
              <div>
                <label className="form-label">Payment Terms</label>
                <div className="space-y-3">
                  {/* Auto-generated payment term (readonly) */}
                  <div>
                    <input
                      type="text"
                      {...register('paymentTerms')}
                      className="form-input bg-gray-50"
                      placeholder="Payment due within 30 days"
                      readOnly
                    />
                    <p className="mt-1 text-xs text-gray-500">Auto-generated based on due date</p>
                  </div>
                  
                  {/* Additional payment terms */}
                  {additionalPaymentTerms.map((term, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={term}
                        onChange={(e) => updateAdditionalPaymentTerm(index, e.target.value)}
                        className="form-input flex-1"
                        placeholder="Additional payment term"
                      />
                      <button
                        type="button"
                        onClick={() => removePaymentTerm(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={addPaymentTerm}
                    className="btn-secondary w-full flex items-center justify-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Payment Term</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Form Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-end space-x-4"
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={() => window.history.back()}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Creating...' : 'Create Invoice'}</span>
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default CreateInvoice;
